import { Select, Switch, Typography } from "antd";
import { useState } from "react";
import { useIntl } from "react-intl";
import { Fragment } from "react/jsx-runtime";
import CustomSelect from "~components/CustomSelect";
import { UniformItemBottomTypes } from "~enums/uniformItemBottomTypes";
import { UniformItemColors } from "~enums/uniformItemColors";
import { UniformItemTopTypes } from "~enums/uniformItemTopTypes";
import { UniformItemTypes } from "~enums/uniformItemTypes";
import type { UniformConfigurationItem } from "~types/uniformConfigurationItem";
import { getColorPickerValue } from "~utilities/getColorPickerValue";
import { isHexBlack } from "~utilities/isHexBlack";
import type { FormType } from "../../models/formModel";
import {
  UniformItemLongSleeveSvg,
  UniformItemShortSleeveSvg,
  UniformItemShortsSvg,
  UniformItemSkirtsSvg,
  UniformItemSleevelessSvg,
  UniformItemSocksSvg,
} from "../../svgs";
import styles from "./UniformSet.module.css";

type Props = {
  uniformConfig: UniformConfigurationItem;
  bottomType?: UniformItemBottomTypes;
  uniformParts: FormType["uniformParts"];
};

export const UniformSet = ({ uniformConfig, bottomType, uniformParts }: Props) => {
  const { formatMessage } = useIntl();

  const isSkirtUniform = bottomType === UniformItemBottomTypes.Skirts;

  const [topType, setTopType] = useState<UniformItemTopTypes>(
    uniformConfig.topType[0] ?? UniformItemTopTypes.ShortSleeve,
  );

  return (
    <div className={styles.mainColumn}>
      <div className={styles.uniformItemsColumn}>
        <Typography.Title level={5}>{formatMessage({ id: uniformConfig.titleIntlKey })}</Typography.Title>
        {Object.entries(uniformConfig.itemTypes).map((entry, partsIdx) => {
          const itemType = entry[0] as UniformItemTypes;

          const isTop =
            itemType === UniformItemTypes.Top ||
            itemType === UniformItemTypes.GoalkeeperTop ||
            itemType === UniformItemTypes.WarmUpTop;

          const isBottom =
            itemType === UniformItemTypes.Bottom ||
            itemType === UniformItemTypes.GoalkeeperBottom ||
            itemType === UniformItemTypes.WarmUpBottom;

          const isSocks = itemType === UniformItemTypes.Socks;

          const colorPrimary = `#${getColorPickerValue(uniformParts?.[itemType]?.colors?.[UniformItemColors.Primary]) ?? "000000"}`;
          const colorSecondary = `#${getColorPickerValue(uniformParts?.[itemType]?.colors?.[UniformItemColors.Secondary]) ?? "000000"}`;
          const outlineColor = isHexBlack(colorPrimary) ? "#ffffff" : "#040504";

          const sharedColors = {
            colorPrimary,
            outlineColor,
          };

          return (
            <Fragment key={partsIdx}>
              {isTop && (
                <>
                  {topType === UniformItemTopTypes.ShortSleeve && (
                    <UniformItemShortSleeveSvg {...sharedColors} colorSecondary={colorSecondary} />
                  )}
                  {topType === UniformItemTopTypes.Sleeveless && <UniformItemSleevelessSvg {...sharedColors} />}
                  {topType === UniformItemTopTypes.LongSleeve && (
                    <UniformItemLongSleeveSvg {...sharedColors} colorSecondary={colorSecondary} />
                  )}
                </>
              )}
              {isBottom && !isSkirtUniform && (
                <div className={styles.bottomContainer}>
                  <UniformItemShortsSvg {...sharedColors} />
                </div>
              )}
              {isBottom && isSkirtUniform && (
                <div className={styles.bottomContainer}>
                  <UniformItemSkirtsSvg {...sharedColors} />
                </div>
              )}
              {isSocks && <UniformItemSocksSvg {...sharedColors} />}
            </Fragment>
          );
        })}
      </div>
      <div>
        {uniformConfig.topType.length === 2 && (
          <Switch
            checkedChildren={formatMessage({ id: `uniform.${uniformConfig.topType[1]}` })}
            unCheckedChildren={formatMessage({ id: `uniform.${uniformConfig.topType[0]}` })}
            checked={topType === uniformConfig.topType[1]}
            onChange={(checked) => setTopType(checked ? uniformConfig.topType[1] : uniformConfig.topType[0])}
          />
        )}
        {uniformConfig.topType.length > 2 && (
          <CustomSelect
            onChange={(value) => setTopType(value as UniformItemTopTypes)}
            defaultValue={topType}
            className={styles.select}
          >
            {uniformConfig.topType.map((topType) => (
              <Select.Option key={topType} value={topType}>
                {formatMessage({ id: `uniform.${topType}` })}
              </Select.Option>
            ))}
          </CustomSelect>
        )}
      </div>
    </div>
  );
};
